import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LandingLayout from "./pages/landing/components/LandingLayout";
import LandingPage from "./pages/landing/LandingPage";
import PrivateRoute from "./components/PrivateRoute";

import React, { useState, useEffect, lazy, Suspense } from "react";
import './index.css';
import { AppMessageProvider } from "./components/common/AppMessageProvider";
import './mobile.css'
import { ConfigProvider } from "antd";
import ReactGA from "react-ga4";
import { RouteTracker } from "./components/common/RouteTracker";

// Trang chủ nạp ngay; các trang khác (đăng nhập, dashboard, công cụ, cổng phụ huynh...) tách thành file riêng
// và chỉ tải khi mở tới → khách vào landing không phải tải cả ứng dụng quản lý
const LoginPage = lazy(() => import("./pages/LoginPage"));
const DashboardLayout = lazy(() => import("./components/DashboardLayout"));
const MobileLayout = lazy(() => import("./components/MobileLayout"));
const PrivacyPolicyPage = lazy(() => import("./pages/landing/PrivacyPolicyPage"));
const TermsOfUsePage = lazy(() => import("./pages/landing/TermsOfUsePage"));
const UserGuidePage = lazy(() => import("./pages/landing/UserGuidePage"));
const CommunityPage = lazy(() => import("./pages/landing/CommunityPage"));
const SystemStatusPage = lazy(() => import("./pages/landing/SystemStatusPage"));
const ContactPage = lazy(() => import("./pages/landing/ContactPage"));
const ToolsLandingPage = lazy(() => import("./pages/landing/ToolsLandingPage"));
const QRGenerator = lazy(() => import("./pages/tools/QrGenTool"));
const WheelOfNamesTool = lazy(() => import("./pages/tools/WheelOfNamesTool"));
const VtfLookupToolPage = lazy(() => import("./pages/tools/VtfLookupToolPage"));
const VtfExcelFillToolPage = lazy(() => import("./pages/tools/VtfExcelFillToolPage"));
const VtfExamLookupToolPage = lazy(() => import("./pages/tools/VtfExamLookupToolPage"));
const VtfMemberCardPage = lazy(() => import("./pages/landing/VtfMemberCardPage"));
const ParentPortalPage = lazy(() => import("./pages/landing/ParentPortalPage"));
const ExamRegistrationPage = lazy(() => import("./pages/landing/ExamRegistrationPage"));
const MobileGoogleCallbackPage = lazy(() => import("./pages/MobileGoogleCallbackPage"));

// Hiện trong lúc tải file của trang (thường < 1s)
const PageLoading = () => (
    <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-slate-200 border-t-orange-500" />
    </div>
);

// Thay bằng Measurement ID thực tế của bạn
const TRACKING_ID = process.env.REACT_APP_GA_ID || "G-9M6PSXMFM6";

const initializeAnalytics = () => {
    try {
        const consentData = localStorage.getItem('vtf_cookie_consent');
        if (consentData) {
            const consent = JSON.parse(consentData);
            if (consent.analytics) {
                ReactGA.initialize(TRACKING_ID);
                console.log("Analytics initialized with consent");
            }
        }
    } catch (e) {
        console.error("Failed to initialize analytics:", e);
    }
};

initializeAnalytics();

const queryClient = new QueryClient();

function App() {
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return (
        <>
            <ConfigProvider
                theme={{
                    token: {
                        borderRadius: 16,
                        fontSize: 14,
                        colorSuccess: '#10b981',
                        colorSuccessBg: '#d1fae5',
                        colorSuccessBorder: '#34d399',
                    },
                }}
            >
                <AppMessageProvider>
                    <QueryClientProvider client={queryClient}>
                        <BrowserRouter>
                            <RouteTracker />
                            <Suspense fallback={<PageLoading />}>
                            <Routes>
                                <Route element={<LandingLayout />}>
                                    <Route path="/" element={<LandingPage />} />
                                    <Route path="/chinh-sach-bao-mat" element={<PrivacyPolicyPage />} />
                                    <Route path="/dieu-khoan-su-dung" element={<TermsOfUsePage />} />
                                    <Route path="/huong-dan-su-dung" element={<UserGuidePage />} />
                                    <Route path="/cong-dong-vo-su" element={<CommunityPage />} />
                                    <Route path="/trang-thai-he-thong" element={<SystemStatusPage />} />
                                    <Route path="/lien-he" element={<ContactPage />} />
                                    <Route path="/cong-cu" element={<ToolsLandingPage />} />
                                    <Route path="/cong-cu/tao-ma-qr" element={<QRGenerator />} />
                                    <Route path="/cong-cu/vong-quay-may-man" element={<WheelOfNamesTool />} />
                                    <Route path="/cong-cu/tra-cuu-vtf" element={<VtfLookupToolPage />} />
                                    <Route path="/cong-cu/tra-cuu-ket-qua-thi" element={<VtfExamLookupToolPage />} />
                                    <Route path="/cong-cu/dien-ten-vtf" element={<VtfExcelFillToolPage />} />

                                    <Route path="/vtf-member/:hash" element={<VtfMemberCardPage />} />
                                    <Route path="/tra-cuu-phu-huynh/:token?" element={<ParentPortalPage />} />
                                </Route>
                                <Route path="/dang-ky-thi" element={<ExamRegistrationPage />} />
                                <Route path="/login" element={<LoginPage />} />
                                <Route path="/mobile-google-callback" element={<MobileGoogleCallbackPage />} />
                                {!isMobile ? (
                                    <Route
                                        path="/dashboard/:tab?"
                                        element={
                                            <PrivateRoute>
                                                <DashboardLayout />
                                            </PrivateRoute>
                                        }
                                    />
                                ) : (
                                    <Route
                                        path="/dashboard/:tab?"
                                        element={
                                            <PrivateRoute>
                                                <MobileLayout />
                                            </PrivateRoute>
                                        }
                                    />
                                )}
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                            </Suspense>
                        </BrowserRouter>
                    </QueryClientProvider>
                </AppMessageProvider>
            </ConfigProvider>
        </>
    )

}

export default App;
